import { Center, Grid, Group, Radio } from '@mantine/core';
import { useForm } from '@mantine/form';
import { TFunction } from 'i18next';
import { useMask } from '@react-input/mask';

import { Button, Modal, Select, Text, TextInput } from 'components';
import { ProfileResponse, UserRequest } from 'types';
import { useEffect } from 'react';
import { AppUtils } from 'utils';
import { MASK_SWISS_PHONE_NUMBER } from 'constant/app';

interface UserFormProps {
  error?: string;
  item: UserRequest;
  profiles: ProfileResponse[];
  loading?: boolean;
  opened: boolean;
  onClose: () => void;
  onSubmit: (data: UserRequest) => void;
  t: TFunction;
  title?: string;
}

const UserForm = ({ error, item, loading, opened, onClose, onSubmit, t, title, profiles }: UserFormProps) => {
  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });

  useEffect(() => {
    if (opened) form.setValues(item);
    else form.reset();
  }, [opened])

  const form = useForm({
    initialValues: item,
    validate: {
      firstName: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      lastName: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      countryCode: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      phoneNumber: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      profile: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      email: (value: any) => AppUtils.isEmpty(value) || !AppUtils.isEmailValid(value) ? t('error.required') : null,
    }
  });


  return (
    <Modal opened={opened} onClose={onClose} title={title}>
      {error && <Text c={'var(--mantine-color-error)'} mb={'md'}>{error}</Text>}
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            label={t('user.firstName.label')}
            key={form.key('firstName')}
            {...form.getInputProps('firstName')}
            required={true}
            disabled={loading}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t('user.lastName.label')}
            key={form.key('lastName')}
            {...form.getInputProps('lastName')}
            required={true}
            disabled={loading}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            leftSection={<Text size={'xs'} pt={2}>{item.countryCode}</Text>}
            label={t('user.phone.label')}
            key={form.key('phoneNumber')}
            {...form.getInputProps('phoneNumber')}
            required={true}
            disabled={loading}
            ref={phoneNumberRef}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t('user.email.label')}
            key={form.key('email')}
            {...form.getInputProps('email')}
            required={true}
            disabled={loading || !!item.id}
          />
        </Grid.Col>
      </Grid>
      <Radio.Group
        name={'profile'}
        label={t('user.profile.label')}
        withAsterisk={true}
        defaultValue={item.profile?.code}
        mt={'sm'}
      >
        <Group mt={4}>
          {profiles.map(el => el.code !== 'CLIENT' &&
            <Radio
              label={t('user.profile.' + el.code)}
              value={el.code}
              disabled={loading}
              onChange={() => {
                const selected = profiles.find(item => item.code === el.code);
                if (selected) form.setFieldValue('profile', selected)
              }}
            />
          )}
        </Group>
      </Radio.Group>
      {item.id && item.status !== 'NOT_VERIFIED' &&
        <Select
          label={t('user.status.label')}
          data={[
            { value: 'ACTIVE', label: t('user.status.ACTIVE') },
            { value: 'SUSPENDED', label: t('user.status.SUSPENDED') }
          ]}
          key={form.key('status')}
          {...form.getInputProps('status')}
          mt={'sm'}
        />
      }
      <Center mt={'xl'}>
        <Button
          disabled={loading || !form.isValid()}
          loading={loading}
          onClick={() => onSubmit(form.getTransformedValues())}>
            {t('action.submit')}
        </Button>
      </Center>
    </Modal>
  )
}

export default UserForm;